<template>
  <div
    class="py-4 md:p-6 flex items-start border-b border-gray-200 md:border-0 gap-4 md:rounded-2xl transition-300 cursor-pointer hover:md:shadow-[0_0_0.875rem_-0.25rem_rgba(0,0,0,0.05),0_2rem_3rem_-0.5rem_rgba(0,0,0,0.05)]"
  >
    <div
      :class="[
        !notificationType?.is_read ? 'bg-gray-200' : notificationType?.bg,
      ]"
      class="flex-y-center justify-center flex-shrink-0 w-12 h-12 md:w-[60px] md:h-[60px] rounded-full"
    >
      <span
        class="text-xl"
        :class="[
          notificationType?.icon,
          !notificationType?.is_read ? '!text-gray-450' : '',
        ]"
      />
    </div>
    <div class="w-full flex items-start justify-between flex-col gap-1">
      <div class="w-full flex items-start justify-between">
        <div class="flex-y-center gap-1">
          <span
            class="text-base font-semibold"
            :class="[
              !notificationType?.is_read ? 'text-gray-450' : 'text-dark',
            ]"
          >
            {{ notificationType?.title }}
          </span>
          <span
            class="flex w-2 h-2 bg-red rounded-full"
            v-if="notificationType?.is_read"
          />
        </div>
        <span class="text-xs text-gray-380">{{
          dayjs(notificationType?.created_at).locale(locale).fromNow()
        }}</span>
      </div>
      <div>
        <i18n-t
          v-if="
            notification?.notification?.notification_template?.type ===
              'trade_open' && notification?.notification?.trade
          "
          keypath="open_rate_message"
          tag="p"
          class="text-sm font-semibold line-clamp-2"
          :class="[
            !notificationType?.is_read ? 'text-gray-450' : 'text-gray-450',
          ]"
        >
          <template #robot>
            <span class="text-dark">{{ notificationType?.robot }}</span>
          </template>
          <template #amount>
            <span class="text-dark">{{
              formatMoneyExact(notificationType?.amount)
            }}</span>
          </template>
          <template #pair>
            <span class="text-dark">{{ notificationType?.pair }}</span>
          </template>
          <template #open_rate>
            <span class="text-dark">{{
              formatMoneyExact(notificationType?.open_rate)
            }}</span>
          </template>
        </i18n-t>
        <i18n-t
          v-if="
            notification?.notification?.notification_template?.type ===
              'trade_close' && notification?.notification?.trade
          "
          keypath="close_rate_message"
          tag="p"
          class="text-sm font-semibold line-clamp-2"
          :class="[
            !notificationType?.is_read ? 'text-gray-450' : 'text-gray-450',
          ]"
        >
          <template #robot>
            <span class="text-dark">{{ notificationType?.robot }}</span>
          </template>
          <template #pair>
            <span class="text-dark">{{ notificationType?.pair }}</span>
          </template>
          <template #close_rate>
            <span class="text-dark">{{
              formatMoneyExact(notificationType?.close_rate)
            }}</span>
          </template>
        </i18n-t>
        <span
          v-if="!notification?.notification?.trade"
          class="text-sm font-semibold line-clamp-2"
          :class="[!notificationType?.is_read ? 'text-gray-450' : 'text-dark']"
        >
          {{ notificationType?.message }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatMoneyExact } from "@/utils";
import { INotification } from "@/modules/Notification/types";
import dayjs from "dayjs";
import { useI18n } from "vue-i18n";
import { computed } from "vue";

interface Props {
  notification: INotification[];
}
const props = defineProps<Props>();
const { t } = useI18n();
const { locale } = useI18n();

const notificationType = computed(() => {
  switch (props?.notification?.notification?.notification_template?.type) {
    case "money_request_rejected":
      return {
        bg: "bg-[#fde6d7]",
        title: t("money_request_rejected"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-card-remove text-[#F04C1A]",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "money_request_accepted":
      return {
        bg: "bg-[#dff9e8]",
        title: t("money_request_accepted"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-card-tick text-[#32AE60]",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "referral_request_rejected":
      return {
        bg: "bg-[#fde6d7]",
        title: t("referral_request_rejected"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-user-minus text-[#F04C1A]",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "referral_request_accepted":
      return {
        bg: "bg-[#dff9e8]",
        title: t("referral_request_accepted"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-user-tick text-[#32AE60]",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "balance_less_10_usdt":
      return {
        bg: "bg-[#fde6d7]",
        title: t("balance_less_10_usdt"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-empty-wallet text-yellow",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "balance_equal_0_usdt":
      return {
        bg: "bg-[#fde6d7]",
        title: t("balance_equal_0_usdt"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-empty-wallet text-[#F04C1A]",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "trade_close":
      return {
        bg: "bg-[#fde6d7]",
        title: t("trade_close"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-trade-close text-[#F04C1A]",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "trade_open":
      return {
        bg: "bg-[#dff9e8]",
        title: t("trade_open"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-trade-open text-[#32AE60]",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "plan_expired":
      return {
        bg: "bg-yellow-100",
        title: t("plan_expired"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-plan-expired text-yellow",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "plan_expire_after_7_days":
      return {
        bg: "bg-yellow-100",
        title: t("plan_expire_after_7_days"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-plan-expired text-yellow",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    case "plan_expire_after_1_days":
      return {
        bg: "bg-yellow-100",
        title: t("plan_expire_after_1_days"),
        message: props?.notification?.notification?.message,
        created_at: props?.notification?.created_at,
        icon: "icon-plan-expired text-[#F04C1A]",
        is_read: !props?.notification?.is_read,
        robot: props?.notification?.notification?.bot_title,
        amount: props?.notification?.notification?.trade?.amount,
        pair: props?.notification?.notification?.trade?.pair,
        open_rate: props?.notification?.notification?.trade?.open_rate,
        close_rate: props?.notification?.notification?.trade?.close_rate,
      };
    default:
      return "default";
  }
});
</script>
